<script setup>
import { ref, watch } from 'vue'

import { formatCurrencyValue, parseCurrencyValue } from '@/composables/currency'
import { useReadonlyField } from '@/composables/readonly-form'

const props = defineProps({
  modelValue: {
    type: [Number, String],
    default: null,
  },
  label: {
    type: String,
    default: undefined,
  },
  suffix: {
    type: String,
    default: undefined,
  },
  decimals: {
    type: Number,
    default: null,
  },
  readonly: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const isReadonly = useReadonlyField(() => props.readonly)

const emit = defineEmits(['update:model-value'])

const format = (value) => formatCurrencyValue(value, props.decimals)

const isFocused = ref(false)
const displayValue = ref(format(props.modelValue))

// The value is recomputed elsewhere (price, quantity, the other total): there is no
// single callsite to intercept, so the displayed text follows it — except while typing.
watch(() => props.modelValue, () => {
  if (!isFocused.value) {
    displayValue.value = format(props.modelValue)
  }
})

const onInput = (value) => {
  displayValue.value = value
  emit('update:model-value', parseCurrencyValue(value))
}

const onFocus = () => {
  isFocused.value = true
}

const onBlur = () => {
  isFocused.value = false
  displayValue.value = format(props.modelValue)
}
</script>
<template>
  <q-input
    :model-value="displayValue"
    :label="props.label"
    :suffix="props.suffix"
    inputmode="decimal"
    :readonly="isReadonly"
    input-class="text-right"
    @update:model-value="onInput"
    @focus="onFocus"
    @blur="onBlur"
  />
</template>
